import React, { forwardRef, useCallback } from "react";

import { InputRef } from "antd/es/input";

import SavedAddress from "@mapmycustomers/shared/types/SavedAddress";
import { AutoCompleteAddress, AutoCompleteAddressProps } from "@mapmycustomers/ui";

import { SavedLocationSelectSurface } from "@app/util/analytic/savedLocationsAnalytics";

import useSavedAddressPresets from "./useSavedAddressPresets";

export interface SavedLocationsAutoCompleteAddressProps extends Omit<
  AutoCompleteAddressProps,
  "presets"
> {
  onSavedAddressPick?: (savedAddress: SavedAddress) => void;
  surface?: SavedLocationSelectSurface;
}

const SavedLocationsAutoCompleteAddress = forwardRef<
  InputRef,
  SavedLocationsAutoCompleteAddressProps
>(
  (
    { onBlur, onChange, onPressEnter, onSavedAddressPick, renderOptionSuffix, surface, ...props },
    ref
  ) => {
    const { isSetting, settingType, ...savedAddressPresets } = useSavedAddressPresets({
      onBlur,
      onChange,
      onSavedAddressPick,
      surface,
    });

    // The picker picks the highlighted row in this same keydown, so act once that has rendered.
    // An Enter that starts or ends Set mode is setting Home or Work, so the host does nothing.
    const handlePressEnter = useCallback(() => {
      const wasSetting = isSetting();
      setTimeout(() => {
        if (!wasSetting && !isSetting()) {
          onPressEnter?.();
        }
      });
    }, [isSetting, onPressEnter]);

    return (
      <AutoCompleteAddress
        {...props}
        {...savedAddressPresets}
        onPressEnter={onPressEnter ? handlePressEnter : undefined}
        ref={ref}
        // A Google row in Set mode sets Home or Work, so it isn't also offered as a place to save
        renderOptionSuffix={settingType ? undefined : renderOptionSuffix}
      />
    );
  }
);

export default SavedLocationsAutoCompleteAddress;
